
import React, { useEffect, Suspense } from "react";
import Review2Workspace from "./pages/Review2Workspace";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  disableMaintenance,
  enableMaintenance,
  isMaintenanceActive,
} from "./utils/maintenance";

import Layout from "./components/Layout/Layout";
import ProtectedGalleryLayout from "./components/ProtectedGalleryLayout";
import { useAuthStore } from "./store/authStore";
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorBoundary from "./components/ErrorBoundary";
import PerformanceMonitor from "./components/PerformanceMonitor";
import TwoFASettings from "./pages/TwoFASettings";
import TwoFAVerify from "./pages/TwoFAVerify";
import AdminUserTable from "./pages/AdminUserTable";
import Maintenance from "./pages/Maintenance";
import odooClient from "./services/odooClient";

// Lazy load all routes for code splitting
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const GalleryLayout = React.lazy(() => import("./components/gallery/GalleryLayout"));
const GalleryDashboardPage = React.lazy(() => import("./pages/GalleryDashboardPage"));
const GalleryUpload = React.lazy(() => import("./pages/GalleryUpload"));
const GalleryPendingPhotos = React.lazy(() => import("./pages/PendingPhotos"));
const BulkValidation = React.lazy(() => import("./pages/BulkValidation"));
const ValidatedPhotos = React.lazy(() => import("./pages/ValidatedPhotos"));
const ScheduledPhotos = React.lazy(() => import("./pages/ScheduledPhotos"));
const RejectedPhotos = React.lazy(() => import("./pages/RejectedPhotos"));

const GalleryPlaceholder = React.lazy(() => import("./pages/GalleryPlaceholder"));
const Contacts = React.lazy(() => import("./pages/Contacts"));
const ContactDetail = React.lazy(() => import("./pages/ContactDetail"));
const ContactNotes = React.lazy(() => import("./pages/ContactNotes"));
const ContactHistory = React.lazy(() => import("./pages/ContactHistory"));
const ContactCreate = React.lazy(() => import("./pages/ContactCreate"));
const ArtworksNew = React.lazy(() => import("./pages/ArtworksNew"));
const ArtworksNoImage = React.lazy(() => import("./pages/ArtworksNoImage"));
const ArtworksLowPrice = React.lazy(() => import("./pages/ArtworksLowPrice"));
const ContactsNoPhoneOrEmail = React.lazy(
  () => import("./pages/ContactsNoPhoneOrEmail"),
);
const ArtworkDetail = React.lazy(() => import("./pages/ArtworkDetail"));
const ArtworkCreate = React.lazy(() => import("./pages/ArtworkCreate"));
const Interests = React.lazy(() => import("./pages/Interests"));
const Todos = React.lazy(() => import("./pages/Todos"));
const ArtList = React.lazy(() => import("./pages/ArtList"));
const EditArtList = React.lazy(() => import("./pages/EditArtList"));
const ArtworksSold = React.lazy(() => import("./pages/ArtworksSold"));
const NewArtworks = React.lazy(() => import("./pages/NewArtworks"));
const NewProspects = React.lazy(() => import("./pages/NewProspects"));
const NewClients = React.lazy(() => import("./pages/NewClients"));
const Proposals = React.lazy(() => import("./pages/Proposals"));
const ProposalCreate = React.lazy(() => import("./pages/ProposalCreate"));
const ArtworkProposal = React.lazy(() => import("./pages/ArtworkProposal"));
const Login = React.lazy(() => import("./pages/Login"));
const Register = React.lazy(() => import("./pages/Register"));
const Profile = React.lazy(() => import("./pages/Profile"));
const Documents = React.lazy(() => import("./pages/Documents"));
const BulkDownloadPdfs = React.lazy(() => import("./pages/BulkDownloadPdfs"));
const ArtworkPDFGenerator = React.lazy(() => import("./pages/ArtworkPDFGenerator"));
const ProposalSharedView = React.lazy(() => import("./pages/ProposalSharedView"));
const Landing = React.lazy(() => import("./pages/Landing"));
const ArtListManage = React.lazy(() => import("./pages/ArtListManage"));
const Offers = React.lazy(() => import("./pages/Offers"));
const Artists = React.lazy(() => import("./pages/Artists"));
const DashboardNotification = React.lazy(() => import("./pages/DashboardNotification"));


// Enhanced Query Client with professional caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 3, // Retry failed requests 3 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false, // Prevent unnecessary refetches

      refetchOnMount: "always",

    },
    mutations: {
      retry: 1,
    },
  },
});

// 🔗 NEW: PDF Proxy Component
const PDFProxy: React.FC = () => {
  const params = useParams();

  const pdfSlug = params["*"]; // Get the wildcard part

  useEffect(() => {
    if (pdfSlug) {
      // Redirect to backend PDF service
      window.location.href = `http://localhost:5000/api/pdf/view/${pdfSlug}`;
    }
  }, [pdfSlug]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading PDF...</p>
      </div>
    </div>
  );
};

function App() {
  const { isAuthenticated, user } = useAuthStore();
  // const isSuperAdmin = Boolean(
  //   (user?.permissions || []).some((p: string) =>
  //     String(p).toLowerCase().includes("superuser")
  //   )
  // );
  const isSuperAdmin = Boolean(
    user?.permissions?.includes(
      "artgallery_custom_fields.group_artgallery_superuser",
    ),
  );

  // useEffect(() => {
  //   if (!isAuthenticated) return; // 🔥 LOGIN PAGE SAFE

  //   const checkMaintenance = async () => {
  //     // 🔑 SUPER ADMIN NEVER BLOCKED
  //     if (isSuperAdmin) {
  //       disableMaintenance();
  //       return;
  //     }

  //     try {
  //       const backend =
  //         (odooClient as any)["backendURL"] ||
  //         process.env.REACT_APP_BACKEND_URL ||
  //         "";

  //       const res = await fetch(`${backend}/api/admin/maintenance`, {
  //         credentials: "include",
  //       });

  //       const data = await res.json();

  //       if (data?.maintenance) {
  //         enableMaintenance();
  //         if (window.location.pathname !== "/maintenance") {
  //           window.location.href = "/maintenance";
  //         }
  //       } else {
  //         disableMaintenance();
  //       }
  //     } catch {
  //       // backend unreachable = maintenance
  //       enableMaintenance();
  //       if (window.location.pathname !== "/maintenance") {
  //         window.location.href = "/maintenance";
  //       }
  //     }
  //   };

  //   checkMaintenance();
  // }, [isAuthenticated, isSuperAdmin]);

  // 🔥 SUPER ADMIN SHOULD NEVER BE STUCK IN MAINTENANCE

  // Auth store initializes automatically with zustand persist

  useEffect(() => {
    if (!isAuthenticated) return; // 🔥 login page safe

    // 👑 super admin never blocked
    if (isSuperAdmin) {
      disableMaintenance();
      return;
    }

    // const backend =
    //   (odooClient as any)["backendURL"] ||
    //   process.env.REACT_APP_BACKEND_URL ||
    //   "";


    const backend =
      process.env.REACT_APP_BACKEND_URL &&
      process.env.REACT_APP_BACKEND_URL !== "undefined"
        ? process.env.REACT_APP_BACKEND_URL
        : "https://preprod.operacrm.com";

    const checkMaintenance = async () => {
      try {
        const res = await fetch(`${backend}/api/admin/maintenance`, {
          credentials: "include",
        });

        const data = await res.json();

        if (data?.maintenance) {
          enableMaintenance();
          if (window.location.pathname !== "/maintenance") {
            window.location.href = "/maintenance";
          }
        }
      } catch {
        // backend unreachable → assume maintenance
        enableMaintenance();
        if (window.location.pathname !== "/maintenance") {
          window.location.href = "/maintenance";
        }
      }
    };

    // ✅ run immediately after login
    checkMaintenance();

    // 🔁 keep checking every 5 seconds
    const interval = setInterval(checkMaintenance, 5000);

    return () => clearInterval(interval);
  }, [isAuthenticated, isSuperAdmin]);

  return (
    <ErrorBoundary>

      <QueryClientProvider client={queryClient}>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Auth Routes */}
              <Route
                path="/login"
                element={
                  !isAuthenticated ? (
                    <Login />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />
              <Route
                path="/register"
                element={
                  !isAuthenticated ? (
                    <Register />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />

              <Route
                path="/2fa-verify"
                element={
                  !isAuthenticated ? (
                    <TwoFAVerify />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />

              {/* 🔗 PUBLIC: Clean PDF URLs (no auth required) */}
              <Route path="/pdf/*" element={<PDFProxy />} />
              <Route path="/proposal-view/:id" element={<ProposalSharedView />} />

              {/* Protected Routes */}
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/bulk-downloads" element={<BulkDownloadPdfs />} />

              <Route
                path="/gallery/*"
                element={
                  isAuthenticated ? (
                    <ProtectedGalleryLayout>
                      <GalleryLayout />
                    </ProtectedGalleryLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              >
                <Route index element={<GalleryDashboardPage />} />
                <Route path="upload" element={<GalleryUpload />} />
                <Route path="pending" element={<GalleryPendingPhotos />} />
                <Route path="bulk" element={<BulkValidation />} />
                <Route path="scheduled" element={<ScheduledPhotos />} />
                <Route path="validated" element={<ValidatedPhotos />} />
                <Route path="rejected" element={<RejectedPhotos />} />
                <Route path="marked" element={<GalleryPlaceholder title="⭐ Marked / Favorites" subtitle="Bookmarked photos & artworks" />} />
                <Route path="review_home" element={<Review2Workspace mode="home" />} />
                <Route path="mainpics" element={<Review2Workspace mode="mainpics" />} />
                <Route path="rapid" element={<Review2Workspace mode="rapid" />} />
                <Route path="editor" element={<Review2Workspace mode="editor" />} />
                <Route path="crm_contacts" element={<GalleryPlaceholder title="👤 Contacts (12,840)" subtitle="Clients · Sellers · Galleries" />} />
                <Route path="crm_contact_new" element={<GalleryPlaceholder title="➕ New Contact" subtitle="Create new contact in Odoo CRM" />} />
                <Route path="crm_artworks" element={<GalleryPlaceholder title="🎨 Artworks (38,175)" subtitle="Browse · Search · Create" />} />
                <Route path="crm_artwork_new" element={<GalleryPlaceholder title="➕ New Artwork" subtitle="Create new artwork in Odoo" />} />
                <Route path="crm_proposals" element={<GalleryPlaceholder title="📝 Proposals" subtitle="Create & manage artwork proposals" />} />
                <Route path="crm_orders" element={<GalleryPlaceholder title="📦 Orders" subtitle="Sales orders from Odoo" />} />
                <Route path="crm_artists" element={<GalleryPlaceholder title="👨‍🎨 Artists" subtitle="Artist profiles & counts" />} />
                <Route path="crm_interests" element={<GalleryPlaceholder title="❤️ Interests" subtitle="Track client artwork interests" />} />
                <Route path="crm_todos" element={<GalleryPlaceholder title="✅ Todos" subtitle="Task management" />} />
                <Route path="crm_artlists" element={<GalleryPlaceholder title="📋 Art Lists" subtitle="Curated artwork collections" />} />
                <Route path="report_missing" element={<GalleryPlaceholder title="📋 Missing LR/HR Report" subtitle="Artworks missing LR/HR · Grouped by gallery" />} />
                <Route path="testlr" element={<GalleryPlaceholder title="🧪 Test LR Generator" subtitle="Generate JPG+WebP into test fields · Real fields untouched" />} />
                <Route path="report_artworks_sold" element={<GalleryPlaceholder title="💰 Sold Artworks" subtitle="Sold artworks list" />} />
                <Route path="report_noimage" element={<GalleryPlaceholder title="🖼 No Image Artworks" subtitle="No image artworks list" />} />
                <Route path="report_lowprice" element={<GalleryPlaceholder title="📉 Low Price Artworks" subtitle="Low price artworks list" />} />
                <Route path="report_nophone" element={<GalleryPlaceholder title="📵 Contacts No Phone" subtitle="No phone/email contacts list" />} />
                <Route path="report_newart" element={<GalleryPlaceholder title="🆕 New Artworks" subtitle="New artworks list" />} />
                <Route path="sync" element={<GalleryPlaceholder title="🔄 Sync & Logs" subtitle="System synchronization" />} />
                <Route path="quality" element={<GalleryPlaceholder title="🎯 Quality Config" subtitle="System configuration for quality" />} />
                <Route path="settings" element={<GalleryPlaceholder title="⚙️ Admin Settings" subtitle="System configuration · Admin only" />} />
                <Route path="admin_users" element={<GalleryPlaceholder title="👥 User Management" subtitle="Admin only" />} />
                <Route path="account" element={<GalleryPlaceholder title="🔑 My Account" subtitle="Your profile" />} />
                <Route path="security" element={<GalleryPlaceholder title="🔒 2FA Security" subtitle="Two-factor authentication" />} />
              </Route>

              {/* PUBLIC: landing page */}
              <Route path="/" element={<Landing />} />

              <Route
                element={
                  isAuthenticated ? (
                    <Layout />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              >
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="contacts" element={<Contacts />} />
                <Route path="contacts/create" element={<ContactCreate />} />
                <Route path="contacts/:id" element={<ContactDetail />} />
                <Route path="admin-dashboard" element={<AdminUserTable />} />
                <Route
                  path="dashboard-notification"
                  element={<DashboardNotification />}
                />

                <Route
                  path="contacts/:contactId/notes/:noteId"
                  element={<ContactNotes />}
                />
                <Route
                  path="contacts/:contactId/notes"
                  element={<ContactNotes />}
                />
                <Route
                  path="contacts/:contactId/history/:historyId"
                  element={<ContactHistory />}
                />
                <Route
                  path="contacts/:contactId/history"
                  element={<ContactHistory />}
                />
                <Route path="artworks" element={<ArtworksNew />} />
                <Route path="artworks/create" element={<ArtworkCreate />} />
                <Route path="artworks/:id" element={<ArtworkDetail />} />
                <Route path="artworks-no-image" element={<ArtworksNoImage />} />
                <Route
                  path="artworks-low-price"
                  element={<ArtworksLowPrice />}
                />
                <Route
                  path="contacts-no-phone-or-email"
                  element={<ContactsNoPhoneOrEmail />}
                />
                <Route path="artworks-sold" element={<ArtworksSold />} />
                <Route path="new-artworks" element={<NewArtworks />} />
                <Route path="new-prospects" element={<NewProspects />} />
                <Route path="new-clients" element={<NewClients />} />
                <Route path="interests" element={<Interests />} />
                <Route path="todos" element={<Todos />} />
                <Route path="artists" element={<Artists />} />
                <Route path="artlist" element={<ArtList />} />
                <Route path="artlist/:id" element={<EditArtList />} />
                <Route path="artlist-manage" element={<ArtListManage />} />
                <Route path="offers" element={<Offers />} />
                <Route path="proposals" element={<Proposals />} />
                <Route path="proposals/create" element={<ProposalCreate />} />
                <Route path="proposals/artwork" element={<ArtworkProposal />} />
                <Route
                  path="proposals/artwork/:id"
                  element={<ArtworkProposal />}
                />
                <Route path="artwork-pdf-generator" element={<ArtworkPDFGenerator />} />
                <Route path="profile" element={<Profile />} />
                <Route path="documents" element={<Documents />} />
                <Route path="/2fa-settings" element={<TwoFASettings />} />
              </Route>
            </Routes>
          </Suspense>
        </Router>
        <PerformanceMonitor />
      </QueryClientProvider>

    </ErrorBoundary>
  );
}


export default App;

